import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

/**
 * AuthCallback — handles the redirect back from Emergent OAuth.
 * URL fragment will be: #session_id=<id>
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const processed = useRef(false);  // ref so StrictMode double-mount doesn't double-exchange

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    (async () => {
      // 1. Parse session_id from URL fragment
      const hash = window.location.hash || '';
      const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      const sessionId = params.get('session_id');

      if (!sessionId) {
        navigate('/app/login?error=missing_session', { replace: true });
        return;
      }

      // 2. Exchange session_id for our JWT
      try {
        const data = await api('/auth/google-callback', {
          method: 'POST',
          body: JSON.stringify({ session_id: sessionId }),
        });

        // 3. Persist JWT & user in AuthContext
        await login(data.token, data.user);

        // 4. Decide where to send the user
        const isNew = !!data.is_new;
        const profileComplete = !!data.user?.profile_complete;

        // Clear the hash so refresh doesn't re-process
        window.history.replaceState({}, document.title, window.location.pathname);

        if (isNew || !profileComplete) {
          navigate('/app/setup', { replace: true });
        } else {
          navigate('/app/discover', { replace: true });
        }
      } catch (err) {
        console.error('Google auth callback failed:', err);
        navigate(`/app/login?error=${encodeURIComponent(err.message || 'auth_failed')}`, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFCF9] flex items-center justify-center px-6" data-testid="auth-callback-loading">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#C8553D] mx-auto mb-4 animate-spin" />
        <h1 className="font-serif text-2xl text-[#2B2D42] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Signing you in…
        </h1>
        <p className="text-sm text-[#6D6875]">Hold tight, we&apos;re verifying your Google account.</p>
      </div>
    </div>
  );
}
